/** Tab-local deletion recovery retains one immutable original operation. */
import { ApiError, apiErrorCode, deleteFeedbackPublication, isCurrentFeedbackDeletionScope } from './api';
import { removeDeletedFeedback } from './queryClient';
import type { FeedbackDeletionCommand, FeedbackDeletionResult, FeedbackDeletionScope } from './types';

export type FeedbackDeletionState =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: FeedbackDeletionResult };

function acknowledgement(value: FeedbackDeletionResult): FeedbackDeletionResult {
  if (!value || value.schema_version !== 'feedback-delete-result.v1' || value.deleted !== true
    || Object.keys(value).sort().join(',') !== 'deleted,schema_version') throw new Error('invalid_deletion_acknowledgement');
  return { ...value };
}

export function captureFeedbackDeletionCommand(scope: FeedbackDeletionScope, command: FeedbackDeletionCommand) {
  const capturedScope = { ...scope };
  const serialized = JSON.stringify(command);
  const intent = (): FeedbackDeletionCommand => JSON.parse(serialized) as FeedbackDeletionCommand;
  const current = () => isCurrentFeedbackDeletionScope(capturedScope);
  let state: FeedbackDeletionState = { status: 'ready' };
  let unresolved = false;
  let inFlight: Promise<FeedbackDeletionResult> | null = null;

  async function run(signal?: AbortSignal): Promise<FeedbackDeletionResult> {
    state = { status: 'sending' };
    const original = intent();
    try {
      const value = await deleteFeedbackPublication(original.feedbackId, original.body, { scope: capturedScope, signal });
      const result = acknowledgement(value);
      unresolved = false;
      state = { status: 'accepted', result };
      void Promise.resolve().then(() => {
        if (current()) return removeDeletedFeedback(capturedScope, original.feedbackId);
      }).catch(() => {});
      return { ...result };
    } catch (error) {
      const code = apiErrorCode(error);
      if (code === 'feedback_operation_unresolved' || code === 'feedback_operation_retired'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      // Retirement erased target/digest proof. Neither it nor a later 404 or
      // permission denial can establish the outcome of the original deletion.
      state = code === 'feedback_operation_retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally { inFlight = null; }
  }

  return {
    getState(): FeedbackDeletionState {
      if (!current()) return { status: 'unavailable' };
      return state.status === 'accepted' ? { ...state, result: { ...state.result } } : { ...state };
    },
    getIntent(): FeedbackDeletionCommand | null { return current() ? intent() : null; },
    send(signal?: AbortSignal): Promise<FeedbackDeletionResult> {
      if (!current() || signal?.aborted) return Promise.reject(new DOMException('Feedback context changed', 'AbortError'));
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve({ ...state.result });
      inFlight = run(signal);
      return inFlight;
    },
  };
}
